import {
  Badge,
  Checkbox,
  InputNumberField,
  TextBase,
  TooltipBubble,
} from '@carboncopyco/ui';
import {
  addDaysUnixDate,
  dateUnixFormat,
  getDaysFromNow,
  isFuture,
} from '@carboncopyco/utils';
import { useUpdateEffect } from 'ahooks';
import classNames from 'classnames';
import { Field, type FieldProps, useFormikContext } from 'formik';
import type { ShareDemoModalState } from './ShareDemoModal';
import { DEFAULT_EXPIRATION_PERIOD } from './consts';

export function ExpirationSection<S extends ShareDemoModalState>() {
  const {
    setFieldValue,
    values: { daysToExpire, toggleExpiration, expiresAt },
  } = useFormikContext<S>();

  useUpdateEffect(() => {
    if (toggleExpiration) {
      setFieldValue(
        'expiresAt',
        addDaysUnixDate(daysToExpire || DEFAULT_EXPIRATION_PERIOD)
      );
      setFieldValue('daysToExpire', daysToExpire || DEFAULT_EXPIRATION_PERIOD);
      return;
    }
    setFieldValue('expiresAt', null);
    setFieldValue('daysToExpire', null);
  }, [daysToExpire, toggleExpiration]);

  const willExpireAtFuture = expiresAt && isFuture(expiresAt);
  const expired = !willExpireAtFuture;
  const expirationDaysFromNow = willExpireAtFuture
    ? getDaysFromNow(expiresAt)
    : 0;
  const dateToDisplay = expiresAt
    ? dateUnixFormat(expiresAt, 'MMM D, YYYY').toUpperCase()
    : '';

  return (
    <div className="p-2">
      <Field
        type="checkbox"
        name="toggleExpiration"
      >
        {({ field: { checked, ...rest } }: FieldProps) => (
          <div className="flex items-center gap-2 just">
            <Checkbox
              {...rest}
              onChange={() => {
                setFieldValue('toggleExpiration', !checked);
              }}
              checked={!!checked}
              name="skipWelcome"
              label={
                <TextBase
                  color="text-ds-natural-600"
                  label="Expiration date"
                />
              }
            />
            <div
              className={classNames('ml-2.5 flex-items-center', {
                hidden:
                  (toggleExpiration && expired) ||
                  (!toggleExpiration && willExpireAtFuture),
              })}
            >
              {expired && expiresAt ? (
                <TooltipBubble
                  placement="top"
                  title={`Expired at ${dateUnixFormat(expiresAt, 'MMM D, YYYY')}`}
                >
                  <Badge
                    variant="grey"
                    label="Expired"
                  />
                </TooltipBubble>
              ) : (
                <Badge
                  variant={expirationDaysFromNow < 4 ? 'yellow' : 'blue'}
                  label={dateToDisplay}
                />
              )}
            </div>
          </div>
        )}
      </Field>
      {toggleExpiration && (
        <div className="flex mt-2.5 items-center">
          <TextBase
            color="text-ds-natural-600"
            label="Expires in"
          />
          <div className="w-16 px-1.5">
            <InputNumberField
              placeholder={DEFAULT_EXPIRATION_PERIOD.toString()}
              noAddedHeight
              name="daysToExpire"
            />
          </div>
          <TextBase
            color="text-ds-natural-600"
            label="days"
          />
        </div>
      )}
    </div>
  );
}
